import { useState } from 'react';
import { addDays, format, parseISO, subDays } from 'date-fns';
import { TransactionsLists } from 'vopay';
import { api } from '@/services/api.config';

/**
 * One of the account's transactions, read through the account's own bearer. `/account/transactions`
 * only answers inside a date window, so the caller passes the transaction's own date (the list row
 * carries it) and the window is the day either side of it — enough to absorb the account's timezone.
 */
export default function useFetchAccountTransaction() {
  const [fetching, setFetching] = useState(false);
  // Undefined until the first read resolves, so a consumer can tell "still loading" from "looked and
  // found nothing" — null is the second of those.
  const [transaction, setTransaction] = useState<TransactionsLists | null | undefined>(undefined);

  async function fetchAccountTransaction(transactionID: string, date: string, bearer: string) {
    try {
      setFetching(true);

      const day = parseISO(date.slice(0, 10));

      const res = await api.fetchTransactionsHistory(
        {
          TransactionID: transactionID,
          StartDateTime: format(subDays(day, 1), 'yyyy-MM-dd'),
          EndDateTime: format(addDays(day, 1), 'yyyy-MM-dd'),
          // The row may belong to one of the account's sub-accounts (the list can include them).
          IncludeSubaccountTransactions: true,
          Limit: 1,
        },
        undefined,
        bearer,
      );

      if (!res.Success) throw new Error(res.ErrorMessage);

      const found = res.Transactions.find((row) => row.TransactionID === transactionID) ?? null;

      setTransaction(found);

      return found;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setFetching(false);
    }
  }

  return { fetching, transaction, fetchAccountTransaction };
}
